import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject'
import { Observable } from 'rxjs/internal/Observable'
import { catchError, map, tap } from 'rxjs/operators'
import { SlatAssessmentDto } from 'src/app/models/slat/slat-assessment-dto'
import { SlatAssessmentTableGroupByDto } from 'src/app/models/slat/slat-assessment-table-group-dto'
import { SlatAssessmentTableItemsDto } from 'src/app/models/slat/slat-assessment-table-items-dto'
import { SlatGraphDto } from 'src/app/models/slat/slat-graph-dto'
import { SlatHistoryDto } from 'src/app/models/slat/slat-history-dto'
import { ApplicationInsightsService } from '../application-insights/application-insights.service'
import { ConfigService } from '../config/config.service'
import { SlatAssessmentItemDto } from '../../models/slat/slat-assessment-item-dto'
import { SlatAssessmentGroupDto } from '../../models/slat/slat-assessment-group-dto'
import { Level } from '../../models/slat/slat-levels-dto'
import { CommonService } from '../common/common.service'

export type SaveMode = 'new' | 'update'

@Injectable({
  providedIn: 'root',
})
export class SlatService {
  private readonly slatInstanceUrl =
    this.config.getSettings('webApi').baseUri + 'slat/'
  private readonly flipInstanceItemsUrl =
    this.slatInstanceUrl + 'getassessmentbyid'
  private readonly assessmentItemsUrl = this.slatInstanceUrl + 'getassessment'
  private readonly submitUrl = this.slatInstanceUrl + 'submit'
  private readonly getLastAssessmentUrl =
    this.slatInstanceUrl + 'getlastassessment'
  private readonly historyUrl = this.slatInstanceUrl + 'history'
  private readonly graphUrl = this.slatInstanceUrl + 'graph'

  private notesValueSource = new BehaviorSubject<string>('')
  notesValue$ = this.notesValueSource.asObservable()
  private readonly flipHistoryUrl = this.slatInstanceUrl + 'history'

  constructor(
    private http: HttpClient,
    private appInsights: ApplicationInsightsService,
    private config: ConfigService,
    private commonService: CommonService,
  ) {}

  SetNoteValue(value: string): void {
    this.notesValueSource.next(value)
  }

  GetLastAssessment(clientId: string): Observable<SlatAssessmentDto> {
    let params: Record<string, any> = { clientId: clientId }
    let result = this.http
      .get<any>(this.getLastAssessmentUrl, { params: params })
      .pipe(
        tap((_) =>
          this.appInsights.logEvent(
            'Retrieved SLAT Assessment from Dataverse.',
          ),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
    return result
  }

  GetAssessmentInstanceById(instanceId: string): Observable<SlatAssessmentDto> {
    let params: Record<string, any> = { slatInstanceId: instanceId }
    let result = this.http
      .get<any>(this.flipInstanceItemsUrl, { params: params })
      .pipe(
        tap((_) =>
          this.appInsights.logEvent(
            'Retrieved SLAT Instance Detail from DataVerse.',
          ),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
    return result
  }

  GetAssessment(clientId: string, type: string): Observable<SlatAssessmentDto> {
    let params: Record<string, any> = { clientId: clientId, type: type }
    return this.http.get<any>(this.assessmentItemsUrl, { params: params }).pipe(
      tap((_) =>
        this.appInsights.logEvent(
          'Retrieved assessment details from DataVerse.',
        ),
      ),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  Submit(assessmentDto: SlatAssessmentDto) {
    // Ensure payload matches backend expectations (e.g., GUIDs/null, strip UI fields)
    const payload = this.normalizeAssessmentForSubmit(assessmentDto)
    return this.http.post<any>(this.submitUrl, payload).pipe(
      tap((_) => this.appInsights.logEvent('Assessment saved.')),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  private normalizeAssessmentForSubmit(
    data: SlatAssessmentDto,
  ): SlatAssessmentDto {
    return {
      ...data,
      groups: (data.groups ?? []).map((group: SlatAssessmentGroupDto) => ({
        ...group,
        items: (group.items ?? []).map((item: SlatAssessmentItemDto) => {
          // Normalize level to a valid GUID or null
          const candidate = item?.level ?? null
          const normalizedLevel =
            item?.levelItem?.levelId ??
            (this.commonService.isValidUUID(candidate) ? candidate : null)

          // Remove UI-only fields from outgoing payload
          const { levelItem, groupName, isCore, levels, ...rest } = item

          return {
            ...rest,
            level: normalizedLevel,
          }
        }),
      })),
    }
  }

  History(clientId: string): Observable<SlatHistoryDto[]> {
    let params: Record<string, any> = { clientId: clientId }
    let result = this.http
      .get<any>(this.flipHistoryUrl, { params: params })
      .pipe(
        map((response: any) => {
          const slatHistory = response as SlatHistoryDto[]
          return slatHistory
        }),
        tap((_) =>
          this.appInsights.logEvent('Retrieved SLAT History from Dataverse'),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
    return result
  }

  GetGraphData(clientId: string): Observable<SlatGraphDto[]> {
    let params: Record<string, any> = { clientId: clientId }
    let result = this.http.get<any>(this.graphUrl, { params: params }).pipe(
      map((response: any) => {
        const slatGraphData = response as SlatGraphDto[]
        return slatGraphData
      }),
      tap((_) =>
        this.appInsights.logEvent('Retrieved SLAT Graph Data from Dataverse'),
      ),
      catchError(async (err) => {
        throw err
      }),
    )
    return result
  }

  SlatFormatMultiHeadingData = (
    slatAssessmentDataDto: SlatAssessmentDto,
  ): (SlatAssessmentTableItemsDto | SlatAssessmentTableGroupByDto)[] => {
    //Formatting the data to display in the table as a multi-header format
    let slatTableData: (
      | SlatAssessmentTableItemsDto
      | SlatAssessmentTableGroupByDto
    )[] = []
    slatAssessmentDataDto.groups.forEach((item) => {
      slatTableData.push({
        isGroupBy: true,
        groupName: item.name,
        headingColumn1: item.headings[0],
        headingColumn2: item.headings[1],
        headingColumn3: item.headings[2],
        headingColumn4: item.headings[3],
        isCore: item.name === 'Core Language and Index Scores',
      })
      item.items.forEach((slatScores) => {
        slatTableData.push({
          name: slatScores.name,
          description: slatScores.description,
          rawScore: slatScores.rawScore,
          score: slatScores.score,
          percentileRank: slatScores.percentileRank,
          level: slatScores.level,
          levelItem: slatScores.levelItem,
          isCore: item.name === 'Core Language and Index Scores',
          templateId: item.templateId,
          groupId: item.groupId,
          itemId: slatScores.itemId,
          levels: item.levels,
          groupName: item.name,
          isGroupBy: false,
        })
      })
    })
    return slatTableData
  }

  updateLevelItem = (data: SlatAssessmentDto): SlatAssessmentDto => ({
    ...data,
    groups: data.groups.map((group: SlatAssessmentGroupDto) => {
      const levels = group.levels ?? []
      // Build a fast lookup map; avoids repeated scans and handles missing levels gracefully
      const levelsById = new Map<string, Level>(
        levels.map((l) => [l.levelId, l]),
      )

      return {
        ...group,
        items: group.items.map((item: SlatAssessmentItemDto) => {
          const key = item.level ?? null
          const match = key
            ? levelsById.get(key) ?? levels.find((l) => l.name === key) ?? null
            : null

          return {
            ...item,
            levelItem: match,
          }
        }),
      }
    }),
  })

  assignHeaders = (data: SlatAssessmentDto): SlatAssessmentDto => {
    //Assigning headers to each group. Presently the headers are not handled in template desing
    data.groups.forEach((item) => {
      if (item.name === 'Assessment Sub-Test') {
        item.headings = [
          'Raw Score',
          'Scaled Score',
          'Percentile Rank',
          'Level',
        ]
      } else if (item.name === 'Core Language and Index Scores') {
        item.headings = [
          'Combined Scaled Score',
          'Standard Score',
          'Percentile Rank',
          'Level',
        ]
      }
    })
    return data
  }
}
